// src/features/tickets/hooks/useTicketDetail.ts
//
// Hook fuer die Ticket-Detail-Ansicht (TicketDetail).
// Kapselt:
//   - Ticket-Laden ueber ticketService.getTicketById
//   - Staff-Liste (fuer Assignee-Dropdown im SidePanel und in der Toolbar)
//   - Aktuellen Mitarbeiter als Actor (memoized, stable identity)
//   - Workflow-Actions updateStatus / updatePriority / assignTicket /
//     updateCustomerAssignment (Kunde, Firmengruppe oder Pool, optional mit
//     Absender-Regel; mit automatischem Reload nach Erfolg, updating-Flag fuer
//     UI-Disable)
//   - reload() fuer manuellen Refresh (z.B. nach Composer-Submit)
//
// UI/Routing bleibt komplett in TicketDetail; dieser Hook ist reine Logik.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../auth';
import { staffService } from '../../../services/staff/StaffService';
import { EmailSenderIdentity } from '../../../services/email/types';
import { ticketService } from '../services/TicketService';
import { supabase } from '../../../shared/services/supabase';
import {
  TicketDetailView,
  TicketActor,
  TicketStatus,
  TicketPriority,
  TicketOperationResult,
  TicketKundenKontext,
  TicketKundenZuordnung
} from '../types';

export interface UseTicketDetailResult {
  // Daten
  ticket: TicketDetailView | null;
  loading: boolean;
  staffList: EmailSenderIdentity[];
  currentStaff: EmailSenderIdentity | null;

  // Actor (memoized — stabile Referenz fuer Composer-Props)
  actor: TicketActor;

  // Workflow-Status
  updating: boolean;

  // Aktionen
  reload: () => Promise<void>;
  updateStatus: (newStatus: TicketStatus) => Promise<TicketOperationResult>;
  updatePriority: (newPriority: TicketPriority) => Promise<TicketOperationResult>;
  assignTicket: (staffId: string | null) => Promise<TicketOperationResult>;
  updateCustomerAssignment: (
    zuordnung: TicketKundenZuordnung,
    kontext: TicketKundenKontext | null
  ) => Promise<TicketOperationResult>;
}

export const useTicketDetail = (ticketId: string | undefined): UseTicketDetailResult => {
  const { user } = useAuth();

  const [ticket, setTicket] = useState<TicketDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState<EmailSenderIdentity[]>([]);
  const [currentStaff, setCurrentStaff] = useState<EmailSenderIdentity | null>(null);
  const [updating, setUpdating] = useState(false);

  // Staff-Liste + aktuellen Mitarbeiter laden
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const all = await staffService.getActiveStaff();
        setStaffList(all);
        if (user?.email) {
          const me = all.find(s => s.email.toLowerCase() === user.email.toLowerCase());
          if (me) setCurrentStaff(me);
        }
      } catch (error) {
        console.error('[useTicketDetail] Staff-Load-Error:', error);
      }
    };
    loadStaff();
  }, [user?.email]);

  // Ticket laden
  // silent=true (Realtime-Refetch): kein loading-Flackern und bei einem
  // voruebergehenden Fehler das vorhandene Ticket behalten statt zu leeren.
  const reload = useCallback(async (silent = false) => {
    if (!ticketId) return;
    if (!silent) setLoading(true);
    try {
      const data = await ticketService.getTicketById(ticketId);
      setTicket(data);
    } catch (error) {
      console.error('[useTicketDetail] Load-Error:', error);
      if (!silent) setTicket(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // reload-Ref haelt stets die aktuelle reload-Funktion, damit das Realtime-Abo
  // nur bei Wechsel der ticketId neu aufgebaut wird.
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  // Live-Aktualisierung (F1): neue Nachrichten in DIESEM Ticket (ticket_messages
  // INSERT, gefiltert auf ticket_id) und Aenderungen am Ticket selbst (tickets
  // UPDATE, gefiltert auf id) loesen entprellt einen Refetch ueber getTicketById
  // aus. Refetch statt Payload-Merge wegen der angereicherten Detail-Form
  // (outbound_mail_log, author_staff, Anhaenge).
  useEffect(() => {
    if (!ticketId) return;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const triggerReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { reloadRef.current(true); }, 400);
    };
    const channel = supabase
      .channel(`ticket-detail-${ticketId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ticket_messages', filter: `ticket_id=eq.${ticketId}` }, triggerReload)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tickets', filter: `id=eq.${ticketId}` }, triggerReload)
      .subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [ticketId]);

  // Actor fuer Service-Calls — memoized, damit Consumer-Komponenten
  // (z.B. TicketReplyComposer) nicht bei jedem Re-Render eine neue
  // Prop-Identitaet bekommen.
  const actor: TicketActor = useMemo(() => ({
    staff_id: currentStaff?.id ?? null,
    staff_email: currentStaff?.email ?? user?.email ?? '',
    staff_full_name: currentStaff?.fullName
  }), [currentStaff, user?.email]);

  const updateStatus = useCallback(async (newStatus: TicketStatus): Promise<TicketOperationResult> => {
    if (!ticket || newStatus === ticket.status) return { success: true };
    setUpdating(true);
    try {
      const result = await ticketService.updateStatus(ticket.id, newStatus, actor);
      await reload();
      return result;
    } finally {
      setUpdating(false);
    }
  }, [ticket, actor, reload]);

  const updatePriority = useCallback(async (newPriority: TicketPriority): Promise<TicketOperationResult> => {
    if (!ticket || newPriority === ticket.priority) return { success: true };
    setUpdating(true);
    try {
      const result = await ticketService.updatePriority(ticket.id, newPriority, actor);
      await reload();
      return result;
    } finally {
      setUpdating(false);
    }
  }, [ticket, actor, reload]);

  const assignTicket = useCallback(async (staffId: string | null): Promise<TicketOperationResult> => {
    if (!ticket || staffId === ticket.assigned_to_staff_id) return { success: true };
    setUpdating(true);
    try {
      const result = await ticketService.assignTicket(ticket.id, staffId, actor);
      await reload();
      return result;
    } finally {
      setUpdating(false);
    }
  }, [ticket, actor, reload]);

  // Kundenzuordnung (Kunde, Firmengruppe oder Pool), optional mit Regel fuer
  // kuenftige Mails. Laedt danach immer neu, auch wenn nur die Regel scheiterte
  // (die Zuordnung selbst ist dann bereits gespeichert).
  const updateCustomerAssignment = useCallback(async (
    zuordnung: TicketKundenZuordnung,
    kontext: TicketKundenKontext | null
  ): Promise<TicketOperationResult> => {
    if (!ticket) return { success: false, error: 'Ticket nicht geladen' };
    setUpdating(true);
    try {
      const result = await ticketService.updateCustomerAssignment(ticket.id, zuordnung, kontext, actor);
      await reload();
      return result;
    } finally {
      setUpdating(false);
    }
  }, [ticket, actor, reload]);

  return {
    ticket,
    loading,
    staffList,
    currentStaff,
    actor,
    updating,
    reload,
    updateStatus,
    updatePriority,
    assignTicket,
    updateCustomerAssignment
  };
};
